import { FundRequestStatus, useListBirthdayRequest, useUpdateRequest, useGetBirthdayRequestDocument } from "@/lib/api/fund";
import React, { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import moment from "moment-timezone";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { CommonDestinationTimezone, formatCurrency, GenerateLoadingOrError } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { safeParseDecimal } from "@/lib/api/common";
import { getReadableStatusLabel, getStatusBadgeColor } from "../common";


export function ViewBirthdayFundRequest() {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const request_id = searchParams.get("request_id");
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showRawContent, setShowRawContent] = useState(false);
  const [formData, setFormData] = useState({
    has_been_granted: false,
    has_been_dispersed: false,
    has_been_reconciled: false,
    amount_granted: "",
    nominated_organization: '',
    nominated_name: '',
    notes: "",
  });

  const requestResult = useListBirthdayRequest(request_id);
  const updateRequest = useUpdateRequest();
  const documentResult = useGetBirthdayRequestDocument(showRawContent ? request_id : null);

  const request = requestResult.data?.data;

  // Initialize form data when request loads
  useEffect(() => {
    if (request) {
      setFormData({
        has_been_granted: request.has_been_granted,
        has_been_dispersed: request.has_been_dispersed,
        has_been_reconciled: request.has_been_reconciled,
        amount_granted: request.amount_granted || '',
        nominated_organization: request.nominated_organization || '',
        nominated_name: request.nominated_name || '',
        notes: request.notes || ''
      });
    }
  }, [request]);

  const handleClose = () => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.delete("request_id");
      return newParams;
    });
    setIsEditing(false);
  };

  const onSubmitEditingForm = async (e: React.FormEvent<HTMLFormElement>) => {
    if (e?.preventDefault) { e.preventDefault(); }
    if (!request_id || !request) { return; }
    if (!isEditing) { return; }
    if (isSaving) { return; }
    setIsSaving(true);

    try {
      const updateResult = await updateRequest({
        id: request_id,
        nominated_organization: formData.nominated_organization,
        nominated_name: formData.nominated_name,
        has_been_granted: formData.has_been_granted,
        has_been_dispersed: formData.has_been_dispersed,
        has_been_reconciled: formData.has_been_reconciled,
        amount_granted: formData.amount_granted ? safeParseDecimal({})(formData.amount_granted).toString() : undefined,
        notes: (!!request.notes && !formData.notes) ? null : formData.notes,
      });

      if (!updateResult.success) {
        throw updateResult;
      }

      toast({
        title: "Updated"
      })

      setIsEditing(false);
    } catch (error: any) {
      console.error("Failed to update form", error);
      toast({
        title: "Failed to update"
      })
    } finally {
      setIsSaving(false);
    }

  };

  if (!request_id) return null;

  const isOpen = !!request_id;

  const loadingOrErrorUi = GenerateLoadingOrError([requestResult]);

  return (
    <>
    <Dialog open={isOpen} onOpenChange={open => !open && handleClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto xl:max-w-[80vw] lg:max-w-[80vw]">
        {loadingOrErrorUi ? (
          loadingOrErrorUi
        ) : !request ? (
          <div className="p-4 text-center">Request not found</div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                <span>Birthday Fund Request</span>
              </DialogTitle>
              <DialogDescription>
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 my-4">
              <Card>
                <CardHeader>
                  <CardTitle>Nominator Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div>
                    <Label className="font-semibold">Name</Label>
                    <p>{request.survey.data.nominator_first_name} {request.survey.data.nominator_last_name}</p>
                  </div>
                  <div>
                    <Label className="font-semibold">Email</Label>
                    <p>{request.survey.data.email}</p>
                  </div>
                  <div>
                    <Label className="font-semibold">Phone</Label>
                    <p>{request.survey.data.phone}</p>
                  </div>
                  <div>
                    <Label className="font-semibold">Nominating Organization</Label>
                    <p>{request.survey.data.nominating_organization}</p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Guardian Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div>
                    <Label className="font-semibold">Name</Label>
                    <p>{request.survey.data.guardian_first_name}</p>
                  </div>
                  <div>
                    <Label className="font-semibold">Phone</Label>
                    <p>{request.survey.data.guardian_phone_number}</p>
                  </div>
                  {request.survey.data.notes && (
                    <div>
                      <Label className="font-semibold">Notes</Label>
                      <p className="whitespace-pre-wrap">{request.survey.data.notes}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Child Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div>
                    <Label className="font-semibold">Birthday</Label>
                    <p>{request.survey.data.birthday_month_of_child} {request.survey.data.birthday_day_of_child}</p>
                  </div>
                  <div>
                    <Label className="font-semibold">Age</Label>
                    <p>{request.survey.data.child_age || "N/A"}</p>
                  </div>
                  <div>
                    <Label className="font-semibold">Party Location</Label>
                    <p>{request.survey.data.party_location || "N/A"}</p>
                  </div>
                  {(request.survey.data.game_date || request.survey.data.game_party_date) && (
                    <div>
                      <Label className="font-semibold">Game/Party Date</Label>
                      <p className="whitespace-pre-wrap">{request.survey.data.game_date ?? request.survey.data.game_party_date}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            <Card className="my-4">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Request Status</span><span>                <Badge className={getStatusBadgeColor(request.status)}>
                    {getReadableStatusLabel(request.status)}
                  </Badge></span>
                  {!isEditing && (
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => setShowRawContent(true)}>
                        View Original Email
                      </Button>
                      <Button size="sm" onClick={() => setIsEditing(true)}>
                        Edit Request
                      </Button>
                    </div>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {isEditing ? (
                  <form onSubmit={onSubmitEditingForm}>
                    <div className="space-y-4">
                      <div className="flex items-center space-x-2">
                        <Label htmlFor="nominated_organization">Nominated Organization</Label>
                        <Input
                          id="nominated_organization"
                          value={formData.nominated_organization || ""}
                          onChange={(e) => setFormData({ ...formData, nominated_organization: e.target.value })}
                        />
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="granted"
                          checked={formData.has_been_granted}
                          onCheckedChange={(checked) => setFormData({ ...formData, has_been_granted: !!checked })}
                        />
                        <Label htmlFor="granted">Request Granted</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          disabled={!formData.has_been_granted}
                          id="dispersed"
                          checked={formData.has_been_dispersed}
                          onCheckedChange={(checked) => setFormData({ ...formData, has_been_dispersed: !!checked })}
                        />
                        <Label htmlFor="dispersed">Funds Dispersed</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          disabled={!formData.has_been_granted}
                          id="has_been_reconciled"
                          checked={formData.has_been_reconciled}
                          onCheckedChange={(checked) => setFormData({ ...formData, has_been_reconciled: !!checked })}
                        />
                        <Label htmlFor="has_been_reconciled">Funds Reconciled</Label>
                      </div>
                      {formData.has_been_granted && (
                        <div className="flex items-center space-x-2">
                          <Label htmlFor="amount_granted">Amount Granted ($)</Label>
                          <Input
                            id="amount_granted"
                            type="number"
                            value={formData.amount_granted}
                            onChange={(e) => setFormData({ ...formData, amount_granted: e.target.value })}
                          />
                        </div>
                      )}
                        <div className="flex items-center space-x-2">
                          <Label htmlFor="amount_granted">Notes</Label>
                          <Input
                            id="notes"
                            value={formData.notes}
                            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                          />
                        </div>
                      <div className="flex justify-end gap-4">
                        <Button disabled={isSaving} onClick={() => setIsEditing(false)} variant={"secondary"}>
                          Exit
                        </Button>
                        <Button type="submit" disabled={isSaving}>
                          {isSaving ? "Saving" : "Save"}
                        </Button>
                      </div>
                    </div>
                  </form>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="font-semibold">Nominated Organization</Label>
                      <p>{request.nominated_organization || "Not specified"}</p>
                    </div>
                    <div>
                      <Label className="font-semibold">Request Granted</Label>
                      <p>{request.has_been_granted ? "Yes" : "No"}</p>
                    </div>
                    {request.has_been_granted && (
                      <div>
                        <Label className="font-semibold">Amount Granted</Label>
                        <p>{request.amount_granted ? formatCurrency(parseFloat(request.amount_granted)) : "-"}</p>
                      </div>
                    )}
                    <div>
                      <Label className="font-semibold">Funds Dispersed</Label>
                      <p>{request.has_been_dispersed ? "Yes" : "No"}</p>
                    </div>
                    <div>
                      <Label className="font-semibold">Funds Reconciled</Label>
                      <p>{request.has_been_reconciled ? "Yes" : "No"}</p>
                    </div>
                    <div>
                      <Label className="font-semibold">Created At</Label>
                      <p>{moment.tz(request.created_at, CommonDestinationTimezone).format("MM/DD/YYYY")}</p>
                    </div>
                    <div>
                      <Label className="font-semibold">Granted Date</Label>
                      <p>{request.granted_date ? moment.tz(request.granted_date, CommonDestinationTimezone).format("MM/DD/YYYY") : "--"}</p>
                    </div>
                    <div>
                      <Label className="font-semibold">Notes</Label>
                      <p>{request.notes ?? ""}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {request.survey.errors && request.survey.errors.length > 0 && (
              <Card className="my-4 bg-red-50">
                <CardHeader>
                  <CardTitle>Survey Errors</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="list-disc pl-5 space-y-1">
                    {request.survey.errors.map((error: any, index: number) => (
                      <li key={index} className="text-red-600">{error}</li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            <DialogFooter>
              {isEditing ? (
                <>
                </>
              ) : (
                <Button onClick={handleClose}>
                  Close
                </Button>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
    
    <Dialog open={showRawContent} onOpenChange={setShowRawContent}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Raw S3 Document Content</DialogTitle>
          <DialogDescription>
            Original form submission data
          </DialogDescription>
        </DialogHeader>
        <div className="mt-4">
          {documentResult?.isLoading ? (
            <div className="text-center py-4">Loading raw content...</div>
          ) : documentResult?.error ? (
            <div className="text-red-600 py-4">Error loading raw content</div>
          ) : documentResult?.data?.data?.document_content ? (
            <pre className="bg-gray-100 p-4 rounded overflow-x-auto text-sm font-mono">
              {documentResult.data.data.document_content}
            </pre>
          ) : (
            <div className="text-center py-4 text-gray-500">No raw content available</div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={() => setShowRawContent(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
